import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Class, Deck, Goal, Score } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card } from '../../components'
import { showToast } from '../../components/Toast'
import { listMyClasses } from '../../lib/classes.js'
import { getDeck, listPublishedClassDecks } from '../../lib/decks.js'
import { computeGoalProgress, type GoalProgress } from '../../lib/goal-progress.js'
import { listActiveGoalsForClasses } from '../../lib/goals.js'
import { listMyRecentScores } from '../../lib/scores.js'
import styles from './StudentHomePage.module.css'

interface ClassDecks {
  classInfo: Class
  decks: Deck[]
}

interface RecentActivity {
  score: Score
  deck: Deck | null
}

interface StudentGoal {
  goal: Goal & { classId: string }
  deck: Deck | null
  progress: GoalProgress
}

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatScore(score: Score) {
  const percent = Math.round(score.accuracy * 100)
  const date = new Date(score.endedAt)
  const studiedAt = Number.isNaN(date.getTime()) ? 'recently' : DATE_FORMATTER.format(date)
  return `${percent}% on ${studiedAt}`
}

export default function StudentHomePage(): JSX.Element {
  const { uid } = useAuth()
  const [classDecks, setClassDecks] = useState<ClassDecks[]>([])
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [studentGoals, setStudentGoals] = useState<StudentGoal[]>([])
  const [goalsLoading, setGoalsLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const loadHome = useCallback(async () => {
    if (!uid) return
    setLoading(true)
    setErrorMessage(null)

    try {
      const classes = await listMyClasses(uid)
      const classDeckResults = await Promise.all(
        classes.map(async (classInfo) => ({
          classInfo,
          decks: await listPublishedClassDecks(classInfo.id),
        })),
      )
      setClassDecks(classDeckResults)

      const scores = await listMyRecentScores(uid, 25).catch(() => [])
      const seenDeckIds = new Set<string>()
      const uniqueScores = scores.filter((score) => {
        if (seenDeckIds.has(score.deckId)) return false
        seenDeckIds.add(score.deckId)
        return true
      })
      const activity = await Promise.all(
        uniqueScores.slice(0, 5).map(async (score) => ({
          score,
          deck: await getDeck(score.deckId),
        })),
      )

      setRecentActivity(activity)
      setLoading(false)

      try {
        setGoalsLoading(true)
        const [goals, goalScores] = await Promise.all([
          listActiveGoalsForClasses(classes.map((classInfo) => classInfo.id)),
          listMyRecentScores(uid, 100).catch(() => []),
        ])
        const goalsWithDecks = await Promise.all(
          goals.map(async (goal) => ({
            goal,
            deck: await getDeck(goal.deckId),
            progress: computeGoalProgress(goal, goalScores),
          })),
        )
        setStudentGoals(goalsWithDecks)
      } catch (err) {
        console.warn('Unable to load student goals:', err)
        setStudentGoals([])
      } finally {
        setGoalsLoading(false)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load your study home.'
      setErrorMessage(message)
      showToast(message, 'error')
    } finally {
      setLoading(false)
    }
  }, [uid])

  useEffect(() => {
    void loadHome()
  }, [loadHome])

  const continueItem = useMemo(() => recentActivity[0] ?? null, [recentActivity])
  const totalDecks = useMemo(
    () => classDecks.reduce((total, item) => total + item.decks.length, 0),
    [classDecks],
  )

  if (loading) {
    return (
      <section className={styles.page} aria-labelledby="student-home-title">
        <h2 id="student-home-title">Ready to study</h2>
        <p role="status" aria-live="polite">
          Loading your classes...
        </p>
      </section>
    )
  }

  if (errorMessage) {
    return (
      <section className={styles.page} aria-labelledby="student-home-title">
        <h2 id="student-home-title">Ready to study</h2>
        <div className={styles.stateBlock}>
          <p role="alert">{errorMessage}</p>
          <Button type="button" variant="secondary" onClick={() => void loadHome()}>
            Retry
          </Button>
        </div>
      </section>
    )
  }

  return (
    <div className={styles.page}>
      <section className={styles.hero} aria-labelledby="student-home-title">
        <div>
          <p className={styles.eyebrow}>Student dashboard</p>
          <h2 id="student-home-title">Ready to study</h2>
          <p>
            {totalDecks === 1 ? '1 class deck is ready.' : `${totalDecks} class decks are ready.`}
          </p>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="continue-title">
        <div className={styles.sectionHeader}>
          <h3 id="continue-title">Continue studying</h3>
        </div>
        {continueItem?.deck ? (
          <Card className={styles.continueCard} highlighted>
            <div>
              <p className={styles.muted}>Last studied</p>
              <h4>{continueItem.deck.title || 'Untitled deck'}</h4>
              <p>{formatScore(continueItem.score)}</p>
            </div>
            <Link
              className={styles.primaryLink}
              to={`/student/decks/${continueItem.deck.id}/flashcards`}
            >
              Open flashcards
            </Link>
          </Card>
        ) : (
          <div className={styles.emptyState}>
            <p>No recent sessions yet. Choose a deck from My classes to begin.</p>
          </div>
        )}
      </section>

      <section className={styles.section} aria-labelledby="goals-title">
        <div className={styles.sectionHeader}>
          <h3 id="goals-title">Goals due soon</h3>
        </div>
        {goalsLoading ? (
          <p role="status" aria-live="polite">
            Loading your goals…
          </p>
        ) : studentGoals.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No active goals yet.</p>
          </div>
        ) : (
          <ul className={styles.goalList}>
            {studentGoals.map(({ goal, deck, progress }) => {
              const percent = Math.round(progress.ratio * 100)
              const modePath = goal.mode === 'flashcards' ? 'flashcards' : 'speedmatch'
              return (
                <li key={`${goal.classId}:${goal.id}`} className={styles.goalItem}>
                  <div>
                    <h4>{deck?.title ?? 'Deck goal'}</h4>
                    <p className={styles.muted}>
                      Due {DATE_FORMATTER.format(new Date(goal.dueAt))}
                    </p>
                  </div>
                  <div className={styles.progressBlock}>
                    <progress value={progress.ratio} max={1}>
                      {percent}%
                    </progress>
                    <span>
                      {percent}% — {progress.detail}
                    </span>
                    {progress.reached ? <span className={styles.goalMet}>Goal met</span> : null}
                  </div>
                  <Link
                    className={styles.primaryLink}
                    to={`/student/decks/${goal.deckId}/${modePath}`}
                  >
                    Study now
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className={styles.section} aria-labelledby="my-decks-title">
        <div className={styles.sectionHeader}>
          <h3 id="my-decks-title">My decks</h3>
          <Link className={styles.sectionLink} to="/student/my-decks">
            View all
          </Link>
        </div>
        <Card className={styles.myDecksCard}>
          <p>Create and manage your own study decks.</p>
          <Link className={styles.primaryLink} to="/student/my-decks">
            Open my decks
          </Link>
        </Card>
      </section>

      <section className={styles.section} aria-labelledby="classes-title">
        <div className={styles.sectionHeader}>
          <h3 id="classes-title">My classes</h3>
        </div>
        {classDecks.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No classes are available yet.</p>
          </div>
        ) : (
          <div className={styles.classList}>
            {classDecks.map(({ classInfo, decks }) => (
              <article className={styles.classBlock} key={classInfo.id}>
                <header>
                  <h4>{classInfo.name || 'Class'}</h4>
                  {classInfo.section ? <p>{classInfo.section}</p> : null}
                </header>
                {decks.length === 0 ? (
                  <p className={styles.muted}>No published class decks yet.</p>
                ) : (
                  <ul className={styles.deckList}>
                    {decks.map((deck) => (
                      <li key={deck.id}>
                        <div className={styles.deckRow}>
                          <div>
                            <span>{deck.title || 'Untitled deck'}</span>
                            <span>{deck.cardCount} cards</span>
                          </div>
                          <div className={styles.deckActions}>
                            <Link to={`/student/decks/${deck.id}/flashcards`}>Flashcards</Link>
                            <Link to={`/student/decks/${deck.id}/speedmatch`}>Speed Match</Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={styles.section} aria-labelledby="activity-title">
        <div className={styles.sectionHeader}>
          <h3 id="activity-title">My recent activity</h3>
        </div>
        {recentActivity.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Completed study sessions will appear here.</p>
          </div>
        ) : (
          <ul className={styles.activityList}>
            {recentActivity.map(({ score, deck }) => (
              <li key={score.id}>
                <Link to={`/student/decks/${score.deckId}/flashcards`}>
                  <span>Last studied {deck?.title ?? 'a deck'}</span>
                  <span>{formatScore(score)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
